import React, { useRef } from 'react';
import { Drawer, makeStyles } from '@material-ui/core';
import {
  CapaQuotationChatRoomSpecificAnnotation,
  useFetchMyQuotationUserQuery
} from '@generated/graphql';
import { CustomizedTypography } from '@components/Customized';
import { colorSet, useSnackbar } from '@components/Provider';
import AnnotationDetailCommentItem from './AnnotationDetailCommentItem';
import CommentInput from './CommentInput';
import UserAvatar from './UserAvatar';
import errorMessage from '@utils/errorMessage';
import useViewerApiContext from '../context/useViewerApiContext';

interface AnnotationDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  annotation?: CapaQuotationChatRoomSpecificAnnotation;
}

/*
  컴포넌트는 모바일 디바이스 환경에서만 사용됨 (breakpoint : 'xs' or viewport width : 600px 이하). 
  어노테이션(주석) 상세는 데스크탑 환경에서는 뷰어 Iframe에서 보여주지만, 모바일 디바이스에서는 웹에서 보여줌.
*/

const AnnotationDetailDrawer: React.FunctionComponent<AnnotationDetailDrawerProps> = ({
  open,
  onClose,
  annotation
}) => {
  const { onAddComment } = useViewerApiContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const comment = useRef<string>('');
  const {
    paper,
    drawerHeader,
    commentContainer,
    drawerInputSection
  } = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const { data } = useFetchMyQuotationUserQuery({
    fetchPolicy: 'cache-only'
  });

  const myName =
    data?.fetchMyQuotationUser.data?.managerName ??
    data?.fetchMyQuotationUser.data?.email ??
    '';

  const myEmail = data?.fetchMyQuotationUser.data?.email;

  const addComment = async () => {
    if (!comment.current || !onAddComment) return;

    try {
      await onAddComment({
        annotationId: annotation?.annotationId as string,
        comment: comment.current
      });

      if (inputRef.current) {
        inputRef.current.value = '';
        comment.current = '';
      }
    } catch (e) {
      if ((e.message as string).includes('CHATROOM_NOT_OPENED')) {
        return enqueueSnackbar(
          errorMessage.notOpendChatroomCreateAnnotationComment
        );
      }
      return enqueueSnackbar(errorMessage.createComment);
    }
  };

  return (
    <Drawer classes={{ paper }} open={open} onClose={onClose} anchor="bottom">
      {/* Drawer Header */}
      <div className={drawerHeader}>
        <div className="drawer-handle" />
        <CustomizedTypography
          variant="body1"
          fontWeight="700"
          color="textPrimary"
        >
          주석
        </CustomizedTypography>
      </div>
      {/* Drawer Comment Container */}
      <div className={commentContainer}>
        {(annotation?.comments ?? []).map((comment, index) => {
          return (
            <AnnotationDetailCommentItem
              key={comment.id}
              annotationId={annotation?.annotationId as string}
              comment={comment}
              isOriginComment={index === 0}
              isMyComment={comment.email === myEmail}
            />
          );
        })}
      </div>
      {/* Drawer Comment Input Section */}
      <div className={drawerInputSection}>
        <UserAvatar userName={myName} size={32} />
        <CommentInput
          placeholder="답글을 입력하세요."
          commentRef={comment}
          inputRef={inputRef}
          onAddComment={addComment}
        />
      </div>
    </Drawer>
  );
};

export default AnnotationDetailDrawer;

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
    height: '421px',
    borderRadius: '16px 16px 0px 0px'
  },
  drawerHeader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    borderBottom: `1px solid ${colorSet.gray300}`,
    '& .drawer-handle': {
      position: 'absolute',
      top: 8,
      left: '50%',
      transform: 'translateX(-50%)',
      borderRadius: '50px',
      width: '48px',
      height: '4px',
      backgroundColor: colorSet.gray400
    }
  },
  drawerInputSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '8px 16px',
    height: '60px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '16px'
    },
    '& .MuiTextField-root .MuiOutlinedInput-root.MuiOutlinedInput-adornedEnd': {
      paddingRight: '8px'
    }
  },
  annotationComment: {},
  commentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '16px',
    overflow: 'auto',
    flex: 1,
    borderBottom: `1px solid ${colorSet.gray300}`
  },
  repliedComment: {}
}));
